import Yup from '@/utils/yup'
import { customMethods } from '@/translations/validations'

export const MIN_LENGTH = 9
export const MAX_LENGTH = 50

/**
 * The rules shown to the user as checklist rows, in display order. Each `name`
 * is the Yup test name that reaches `error.types`, which is what binds a row to
 * its rule.
 */
export const DISPLAY_RULES = [
  { name: 'min', label: `${MIN_LENGTH}+ characters` },
  { name: 'disallowSpaces', label: 'No spaces' },
  { name: 'hasLetterChars', label: 'At least 1 letter' },
  { name: 'hasDigits', label: 'At least 1 number' },
  { name: 'hasUppercase', label: 'At least 1 uppercase letter' },
]

/**
 * Rules the schema enforces without a checklist row. They reject through a
 * message beneath the rows instead. Listing them explicitly is what lets the
 * suite prove that every rule the schema declares is either displayed or
 * deliberately excluded, rather than merely forgotten.
 */
export const MESSAGE_ONLY_RULES = [
  { name: 'max', message: `Must be ${MAX_LENGTH} characters or fewer` },
  { name: 'printableAscii', message: customMethods.printableAscii },
]

export const RULE_STATE = {
  NEUTRAL: 'neutral',
  SATISFIED: 'satisfied',
  FAILED: 'failed',
}

export const passwordRules = () => Yup
  .string()
  .required()
  .disallowSpaces()
  .min(MIN_LENGTH)
  .max(MAX_LENGTH)
  .hasLetterChars()
  .hasDigits()
  .hasUppercase()
  .printableAscii()

export const PASSWORD_MISMATCH_MESSAGE = 'Passwords must match'

/**
 * The confirm-password rule, exported apart from `passwordRules` so the
 * checklist metadata above is untouched: the checklist lists password rules,
 * and a mismatch between two fields is not one of them.
 *
 * `Yup.ref('password')` only resolves inside an object schema that holds both
 * fields. Validated on its own the ref is `undefined`, so every value fails —
 * including a matching one — which makes the rule meaningful only when composed
 * into a `Yup.object` alongside `password`.
 */
export const confirmPasswordRule = () => Yup
  .string()
  .oneOf([Yup.ref('password')], PASSWORD_MISMATCH_MESSAGE)

/**
 * Three-state derivation for the checklist rows.
 *
 * `types` alone cannot express "not checked yet": a rule that an empty string
 * vacuously satisfies never appears in it, so it is indistinguishable from a
 * rule that genuinely passed. The field value is the only thing that separates
 * the two, which is why it is the first argument and not an optimisation.
 *
 * Soundness for a non-empty value depends on validation having run for that
 * value. That holds here because the field starts empty and validates on
 * change, so a non-empty value has always been through the resolver.
 */
export const ruleStates = (value, types) => {
  const failed = types || {}

  return DISPLAY_RULES.map(({ name, label }) => {
    if (!value) return { name, label, state: RULE_STATE.NEUTRAL }

    return {
      name,
      label,
      state: failed[name] ? RULE_STATE.FAILED : RULE_STATE.SATISFIED,
    }
  })
}

/**
 * Messages for the rules that have no checklist row. Without these a password
 * that fails only these rules shows a full set of green checks and no
 * explanation of why it was rejected.
 */
export const schemaOnlyMessages = (types) => {
  const failed = types || {}

  return MESSAGE_ONLY_RULES
    .filter(({ name }) => failed[name])
    .map(({ message }) => message)
}
